// src/pages/Service/ServiceReturn/ServiceReturnRecord.js
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { App, Button, DatePicker, Flex, Form, Table, Tooltip, Typography } from "antd";
import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import SearchAutoComplete from "../../../components/SearchAutoComplete";
import { fetchCustomersList } from "../../SalesReturn/salesReturnThunks";
import { selectCustomersList } from "../../SalesReturn/salesReturnSelectors";
import { deleteServiceReturnApi, getServiceReturnRecordApi } from "../../../apiservice/service";
import { formatToCompanyTimezone, formatToUtc, getAccessType, numberFormat } from "../../../lib/functions";
import { useTxLock } from "../../../hooks/useTxLock";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const ServiceReturnRecord = () => {
  const { modal } = App.useApp();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLocked, lockDateFormatted } = useTxLock();

  const [range, setRange] = useState([dayjs().startOf("month"), dayjs().endOf("day")]);
  const [customer, setCustomer] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const accessType = getAccessType("service_return_record");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getServiceReturnRecordApi({
        customerId: customer?.acc_id || null,
        // created_date is stored as "YYYY-MM-DD HH:mm:ss" UTC; formatToUtc reads
        // these strings in the company time zone and returns that same format.
        fromDate: range?.[0] ? formatToUtc(range[0].format("YYYY-MM-DD 00:00:00")) : null,
        toDate: range?.[1] ? formatToUtc(range[1].format("YYYY-MM-DD 23:59:59")) : null,
      });
      setRows(res.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [customer, range]);

  useEffect(() => {
    load();
  }, []);

  const handleDelete = (record) =>
    modal.confirm({
      title: "Are you sure?",
      content: `This will delete service return ${record.service_r_voucher_no} and reverse its refund.`,
      okText: "Yes, Delete",
      cancelText: "Cancel",
      onOk: async () => {
        const res = await deleteServiceReturnApi({ service_r_id: record.service_r_id });
        if (res.data?.error) {
          modal.warning({ title: res.data.message || "Delete failed" });
          return;
        }
        modal.success({ title: "Service return deleted successfully" });
        load();
      },
    });

  const columns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Return No.", dataIndex: "service_r_voucher_no" },
    {
      title: "Date",
      dataIndex: "created_date",
      render: (v) => formatToCompanyTimezone(v)?.split(" ")[0],
    },
    { title: "Service Voucher", dataIndex: "service_voucher_no" },
    { title: "Customer", dataIndex: "acc_name" },
    { title: "Return Total", dataIndex: "total_amount", align: "right", render: (v) => numberFormat(v) },
    { title: "Refund", dataIndex: "paid_amount", align: "right", render: (v) => numberFormat(v) },
    { title: "Narration", dataIndex: "narration" },
    { title: "Created By", dataIndex: "full_name" },
    {
      title: "Actions",
      render: (_, record) => {
        const locked = isLocked(record.created_date);
        return (
          <>
            <Tooltip title="View voucher">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/service-return?tab=voucher&id=${record.service_r_id}`)}
              />
            </Tooltip>
            <Tooltip
              title={locked ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.` : "Delete"}
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                disabled={accessType !== "WRITE" || locked}
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          </>
        );
      },
    },
  ];

  return (
    <div>
      <Title level={3}>Service Return Record</Title>
      <Form layout="inline" style={{ marginBottom: 24, rowGap: 12 }}>
        <Form.Item label="Date">
          <RangePicker value={range} onChange={setRange} />
        </Form.Item>
        <Form.Item label="Customer" style={{ minWidth: 280 }}>
          <SearchAutoComplete
            showAddNew={false}
            placeholder="Customer"
            searchThunk={(text) => dispatch(fetchCustomersList(text))}
            resultSelector={selectCustomersList}
            onSelect={(value) => setCustomer(value?.acc_id ? value : null)}
          />
        </Form.Item>
        <Flex gap={8}>
          <Button type="primary" onClick={load}>
            Search
          </Button>
        </Flex>
      </Form>
      <Table
        bordered
        loading={loading}
        columns={columns}
        dataSource={rows}
        rowKey="service_r_id"
        size="middle"
        scroll={{ x: "max-content" }}
        summary={(data) => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={5} align="right">
              <b>Total</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="right">
              <b>{numberFormat(data.reduce((s, r) => s + Number(r.total_amount || 0), 0))}</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="right">
              <b>{numberFormat(data.reduce((s, r) => s + Number(r.paid_amount || 0), 0))}</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={7} colSpan={3} />
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default ServiceReturnRecord;
